'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useExplorer } from '@/hooks/useExplorer';
import { listEvents, EventsApiError } from '@/services/events/EventsService';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
import type { ChargebeeEvent, EventApiFilters } from '@/types/events';

/**
 * Polling engine for live-tailing Chargebee events.
 *
 * Behavior:
 * - Polls list-events at a configurable interval (default 10s)
 * - Uses occurred_at[after] with lastSeenTs - 1s to avoid missing same-second events
 * - Deduplicates events by ID using a bounded Set
 * - Applies exponential backoff on 429/5xx errors
 * - Pauses when tab is hidden or browser is offline
 * - New events go into liveBuffer (not directly into main list)
 */

const DEFAULT_INTERVAL_MS = 5_000;
const MAX_INTERVAL_MS = 60_000;
const BACKOFF_MULTIPLIER = 1.5;
const MAX_CONSECUTIVE_ERRORS = 5;
const MAX_SEEN_IDS = 5000;

interface UseEventPollingOptions {
  enabled: boolean;
  filters?: EventApiFilters;
}

export function useEventPolling({ enabled, filters }: UseEventPollingOptions) {
  const { siteId, siteName, apiKey } = useExplorer();
  const {
    events,
    pollingEnabled,
    consecutiveErrors,
    addToLiveBuffer,
    setLastPolledAt,
    setNextPollAt,
    setConsecutiveErrors,
    setPollingEnabled,
  } = useEventExplorerStore();

  const abortControllerRef = useRef<AbortController>(undefined);
  const currentIntervalRef = useRef(DEFAULT_INTERVAL_MS);
  const seenIdsRef = useRef(new Set<string>());
  const lastSeenTsRef = useRef<number | null>(null);
  const isVisibleRef = useRef(true);
  const isOnlineRef = useRef(true);

  // Keep seenIds in sync with current event list
  useEffect(() => {
    const ids = new Set(events.map((e) => e.id));
    // Merge with existing seenIds, capping at MAX_SEEN_IDS
    const combined = new Set([...ids, ...seenIdsRef.current]);
    if (combined.size > MAX_SEEN_IDS) {
      const arr = Array.from(combined);
      seenIdsRef.current = new Set(arr.slice(arr.length - MAX_SEEN_IDS));
    } else {
      seenIdsRef.current = combined;
    }

    // Track the latest timestamp
    if (events.length > 0) {
      const maxTs = Math.max(...events.map((e) => e.occurred_at));
      lastSeenTsRef.current = maxTs;
    }
  }, [events]);

  const deduplicateEvents = useCallback(
    (newEvents: ChargebeeEvent[]): ChargebeeEvent[] => {
      return newEvents.filter((e) => !seenIdsRef.current.has(e.id));
    },
    []
  );

  const poll = useCallback(async () => {
    if (
      !siteId ||
      !siteName ||
      !apiKey ||
      !isVisibleRef.current ||
      !isOnlineRef.current
    ) {
      return;
    }

    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      // Build polling filters: base filters + occurred_at constraint
      const pollFilters: EventApiFilters = { ...filters };
      if (lastSeenTsRef.current) {
        // Request from lastSeenTs - 1s to avoid missing same-second events
        pollFilters.occurredAt = {
          operator: 'after',
          value: lastSeenTsRef.current - 1,
        };
      }

      const response = await listEvents(
        {
          siteId,
          siteName,
          apiKey,
          limit: 25,
          sortBy: 'desc',
          filters:
            Object.keys(pollFilters).length > 0 ? pollFilters : undefined,
        },
        abortControllerRef.current.signal
      );

      const rawEvents = response.list.map((item) => item.event);
      const newEvents = deduplicateEvents(rawEvents);

      if (newEvents.length > 0) {
        // Add new IDs to seen set
        for (const e of newEvents) {
          seenIdsRef.current.add(e.id);
        }
        addToLiveBuffer(newEvents);

        // Update lastSeenTs
        const maxTs = Math.max(...newEvents.map((e) => e.occurred_at));
        if (!lastSeenTsRef.current || maxTs > lastSeenTsRef.current) {
          lastSeenTsRef.current = maxTs;
        }
      }

      // Reset backoff on success
      setConsecutiveErrors(0);
      currentIntervalRef.current = DEFAULT_INTERVAL_MS;
      setLastPolledAt(Date.now());
      setNextPollAt(Date.now() + currentIntervalRef.current);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;

      const newCount = consecutiveErrors + 1;
      setConsecutiveErrors(newCount);

      // Apply exponential backoff
      if (
        err instanceof EventsApiError &&
        (err.isRateLimited || err.isServerError)
      ) {
        currentIntervalRef.current = Math.min(
          currentIntervalRef.current * BACKOFF_MULTIPLIER,
          MAX_INTERVAL_MS
        );
      }

      setNextPollAt(Date.now() + currentIntervalRef.current);

      // Pause after max consecutive errors
      if (newCount >= MAX_CONSECUTIVE_ERRORS) {
        setPollingEnabled(false);
      }
    }
  }, [
    siteId,
    siteName,
    apiKey,
    filters,
    consecutiveErrors,
    deduplicateEvents,
    addToLiveBuffer,
    setLastPolledAt,
    setNextPollAt,
    setConsecutiveErrors,
    setPollingEnabled,
  ]);

  // Track visibility & online state (polling is now driven by the UI countdown
  // in EventFilterBar, not by setInterval here)
  useEffect(() => {
    const handleVisibility = () => {
      isVisibleRef.current = !document.hidden;
      if (document.hidden) {
        abortControllerRef.current?.abort();
      }
    };
    const handleOnline = () => {
      isOnlineRef.current = true;
    };
    const handleOffline = () => {
      isOnlineRef.current = false;
      abortControllerRef.current?.abort();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    isPolling: enabled && pollingEnabled,
    forceRefresh: poll,
  };
}
